/** Rotas da aplicação e proteção por autenticação/permissão. */

import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useAuth } from './auth/AuthContext';
import { ITENS, Layout } from './components/Layout';
import { Card, TituloPagina } from './components/base';
import { Login } from './pages/Login';
import { RedefinirSenha } from './pages/RedefinirSenha';
import { telas } from './rotas';
import { Icone } from './components/Icone';

// Cada tela num pedaço separado do bundle (ver `rotas.ts`). O login fica de
// fora de propósito: é a primeira coisa de quem chega sem sessão.
const Calendario = lazy(() => telas.calendario().then((m) => ({ default: m.Calendario })));
const Chancelaria = lazy(() => telas.chancelaria().then((m) => ({ default: m.Chancelaria })));
const Dashboard = lazy(() => telas.dashboard().then((m) => ({ default: m.Dashboard })));
const Atas = lazy(() => telas.documentos().then((m) => ({ default: m.Atas })));
const Fotos = lazy(() => telas.fotos().then((m) => ({ default: m.Fotos })));
const AlbumDeFotos = lazy(() => telas.fotos().then((m) => ({ default: m.AlbumDeFotos })));
const Gob = lazy(() => telas.documentos().then((m) => ({ default: m.Gob })));
const NotasFiscais = lazy(() => telas.notas().then((m) => ({ default: m.NotasFiscais })));
const Eventos = lazy(() => telas.eventos().then((m) => ({ default: m.Eventos })));
const EventoDetalhe = lazy(() => telas.eventos().then((m) => ({ default: m.EventoDetalhe })));
const ContasBancarias = lazy(() => telas.contas().then((m) => ({ default: m.ContasBancarias })));
const Financeiro = lazy(() => telas.financeiro().then((m) => ({ default: m.Financeiro })));
const Fornecedores = lazy(() => telas.fornecedores().then((m) => ({ default: m.Fornecedores })));
const Irmaos = lazy(() => telas.irmaos().then((m) => ({ default: m.Irmaos })));
const Graduacoes = lazy(() => telas.irmaos().then((m) => ({ default: m.Graduacoes })));
const Orcamento = lazy(() => telas.orcamento().then((m) => ({ default: m.Orcamento })));
const Presencas = lazy(() => telas.presencas().then((m) => ({ default: m.Presencas })));
const PlanoContas = lazy(() => telas.planoContas().then((m) => ({ default: m.PlanoContas })));
const ProjecaoQuadro = lazy(() => telas.projecao().then((m) => ({ default: m.ProjecaoQuadro })));
const Relatorios = lazy(() => telas.relatorios().then((m) => ({ default: m.Relatorios })));
const Tesouraria = lazy(() => telas.tesouraria().then((m) => ({ default: m.Tesouraria })));
const Trabalhos = lazy(() => telas.trabalhos().then((m) => ({ default: m.Trabalhos })));
const Arquivo = lazy(() => telas.arquivo().then((m) => ({ default: m.Arquivo })));
const MeusDocumentos = lazy(() => telas.arquivo().then((m) => ({ default: m.MeusDocumentos })));
const Auditoria = lazy(() => telas.auditoria().then((m) => ({ default: m.Auditoria })));
const Lojas = lazy(() => telas.plataforma().then((m) => ({ default: m.Lojas })));
const PlataformaLoja = lazy(() => telas.plataforma().then((m) => ({ default: m.PlataformaLoja })));
const Cobranca = lazy(() => telas.cobranca().then((m) => ({ default: m.Cobranca })));
const Comunicados = lazy(() => telas.comunicados().then((m) => ({ default: m.Comunicados })));
const Loja = lazy(() => telas.loja().then((m) => ({ default: m.Loja })));
const DatasImportantes = lazy(() => telas.datas().then((m) => ({ default: m.DatasImportantes })));
const Visitacoes = lazy(() => telas.visitas().then((m) => ({ default: m.Visitacoes })));
// Páginas de leitura, abertas a quem ainda não entrou: carregam só se pedidas.
const Termos = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Termos })));
const Privacidade = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Privacidade })));

function TelaCarregando() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--t2)',
        fontSize: 14,
      }}
    >
      Carregando o portal…
    </div>
  );
}

/** Barra o acesso de quem não está autenticado, preservando o destino. */
function ExigeLogin({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useAuth();
  const local = useLocation();

  if (carregando) return <TelaCarregando />;
  if (!usuario) return <Navigate to="/login" state={{ de: local.pathname }} replace />;
  return <>{children}</>;
}

/** Página só é montada se o usuário tiver a permissão exigida. */
function ExigePermissao({ permissao, children }: { permissao: string; children: ReactNode }) {
  const { pode } = useAuth();
  if (!pode(permissao)) {
    return (
      <>
        <TituloPagina titulo="Acesso restrito" />
        <Card>
          <div className="aviso atencao" style={{ marginBottom: 0 }}>
            <Icone nome="cadeado" tamanho={15} /> Seu perfil não tem a permissão <code>{permissao}</code>. Fale com o administrador da
            loja se precisar deste módulo.
          </div>
        </Card>
      </>
    );
  }
  return <>{children}</>;
}

/** A tela inicial de quem entra.
 *
 * "Visão geral" mostra saldo e inadimplência, e deixou de ser de todo mundo
 * quando os perfis passaram a ser por cargo. Quem não a alcança precisa cair
 * em alguma coisa: sem isto, o chanceler e o irmão entravam direto num
 * "Acesso restrito", o que parece sistema quebrado logo no primeiro segundo.
 *
 * O destino sai da própria ordem do menu — o primeiro item que o perfil
 * alcança — para não existir uma segunda lista para manter em dia.
 */
function Inicio() {
  const { pode } = useAuth();
  if (pode('dashboard:ler')) return <Dashboard />;
  const primeiro = ITENS.find((item) => item.para !== '/' && pode(item.permissao));
  return primeiro ? (
    <Navigate to={primeiro.para} replace />
  ) : (
    <ExigePermissao permissao="dashboard:ler">
      <Dashboard />
    </ExigePermissao>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Pública: quem chega pelo link do e-mail não consegue entrar ainda. */}
      <Route path="/redefinir-senha" element={<RedefinirSenha />} />
      <Route
        path="/termos"
        element={
          <Suspense fallback={null}>
            <Termos />
          </Suspense>
        }
      />
      <Route
        path="/privacidade"
        element={
          <Suspense fallback={null}>
            <Privacidade />
          </Suspense>
        }
      />

      <Route
        element={
          <ExigeLogin>
            <Layout />
          </ExigeLogin>
        }
      >
        <Route index element={<Inicio />} />
        <Route
          path="tesouraria"
          element={
            <ExigePermissao permissao="tesouraria:ler">
              <Tesouraria />
            </ExigePermissao>
          }
        />
        <Route
          path="irmaos"
          element={
            <ExigePermissao permissao="quadro:ler">
              <Irmaos />
            </ExigePermissao>
          }
        />
        <Route
          path="chancelaria/sessoes"
          element={
            <ExigePermissao permissao="sessoes:ler">
              <Chancelaria />
            </ExigePermissao>
          }
        />
        <Route
          path="chancelaria/datas"
          element={
            <ExigePermissao permissao="sessoes:escrever">
              <DatasImportantes />
            </ExigePermissao>
          }
        />
        <Route
          path="visitacoes"
          element={
            <ExigePermissao permissao="visitas:proprio">
              <Visitacoes />
            </ExigePermissao>
          }
        />
        <Route
          path="presencas"
          element={
            <ExigePermissao permissao="sessoes:ler">
              <Presencas />
            </ExigePermissao>
          }
        />
        <Route
          path="projecao"
          element={
            <ExigePermissao permissao="quadro:ler">
              <ProjecaoQuadro />
            </ExigePermissao>
          }
        />
        <Route
          path="relatorios"
          element={
            <ExigePermissao permissao="quadro:ler">
              <Relatorios />
            </ExigePermissao>
          }
        />
        <Route
          path="graduacoes"
          element={
            <ExigePermissao permissao="quadro:ler">
              <Graduacoes />
            </ExigePermissao>
          }
        />
        <Route
          path="calendario"
          element={
            <ExigePermissao permissao="sessoes:ler">
              <Calendario />
            </ExigePermissao>
          }
        />
        <Route
          path="fotos"
          element={
            <ExigePermissao permissao="fotos:ler">
              <Fotos />
            </ExigePermissao>
          }
        />
        <Route
          path="fotos/:albumId"
          element={
            <ExigePermissao permissao="fotos:ler">
              <AlbumDeFotos />
            </ExigePermissao>
          }
        />
        <Route
          path="notas-fiscais"
          element={
            <ExigePermissao permissao="notas:ler">
              <NotasFiscais />
            </ExigePermissao>
          }
        />
        <Route
          path="financeiro"
          element={
            <ExigePermissao permissao="financeiro:resumo">
              <Financeiro />
            </ExigePermissao>
          }
        />
        <Route
          path="fornecedores"
          element={
            <ExigePermissao permissao="fornecedores:ler">
              <Fornecedores />
            </ExigePermissao>
          }
        />
        <Route
          path="contas-bancarias"
          element={
            <ExigePermissao permissao="financeiro:ler">
              <ContasBancarias />
            </ExigePermissao>
          }
        />
        <Route
          path="plano-contas"
          element={
            <ExigePermissao permissao="financeiro:ler">
              <PlanoContas />
            </ExigePermissao>
          }
        />
        <Route
          path="orcamento"
          element={
            <ExigePermissao permissao="orcamento:ler">
              <Orcamento />
            </ExigePermissao>
          }
        />
        <Route
          path="gob"
          element={
            <ExigePermissao permissao="gob:ler">
              <Gob />
            </ExigePermissao>
          }
        />
        <Route
          path="eventos"
          element={
            <ExigePermissao permissao="eventos:ler">
              <Eventos />
            </ExigePermissao>
          }
        />
        <Route
          path="eventos/:slug"
          element={
            <ExigePermissao permissao="eventos:ler">
              <EventoDetalhe />
            </ExigePermissao>
          }
        />
        <Route
          path="trabalhos"
          element={
            <ExigePermissao permissao="trabalhos:ler">
              <Trabalhos />
            </ExigePermissao>
          }
        />
        <Route
          path="arquivo"
          element={
            <ExigePermissao permissao="arquivo:ler">
              <Arquivo />
            </ExigePermissao>
          }
        />
        <Route
          path="meus-documentos"
          element={
            <ExigePermissao permissao="arquivo:proprio">
              <MeusDocumentos />
            </ExigePermissao>
          }
        />
        <Route
          path="atas"
          element={
            <ExigePermissao permissao="atas:ler">
              <Atas />
            </ExigePermissao>
          }
        />
        <Route
          path="comunicados"
          element={
            <ExigePermissao permissao="comunicados:ler">
              <Comunicados />
            </ExigePermissao>
          }
        />
        <Route
          path="loja"
          element={
            <ExigePermissao permissao="loja:escrever">
              <Loja />
            </ExigePermissao>
          }
        />
        <Route
          path="auditoria"
          element={
            <ExigePermissao permissao="auditoria:ler">
              <Auditoria />
            </ExigePermissao>
          }
        />
        <Route
          path="plataforma/lojas"
          element={
            <ExigePermissao permissao="plataforma:administrar">
              <Lojas />
            </ExigePermissao>
          }
        />
        <Route
          path="plataforma/lojas/:id"
          element={
            <ExigePermissao permissao="plataforma:administrar">
              <PlataformaLoja />
            </ExigePermissao>
          }
        />
        <Route
          path="plataforma/cobranca"
          element={
            <ExigePermissao permissao="plataforma:administrar">
              <Cobranca />
            </ExigePermissao>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
